import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, setScopedPropertyId } from "@/lib/api";
import {
  readPinnedPropertyId,
  resolveTabPropertyId,
  writePinnedPropertyId,
} from "@/lib/tabProperty";
import { browserLanguage, setLanguage } from "./i18n.js";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = still checking, false = not logged in, object = user
  const [user, setUser] = useState(null);
  const [pending2fa, setPending2fa] = useState(null); // { email } while awaiting TOTP

  const refresh = useCallback(async () => {
    try {
      const me = await api.authMe();
      // Pin this tab to its own property. `active_property_id` on the
      // server is shared by every window of the account; the tab's pin
      // is not, so a switch in another window can't re-scope this one.
      const tabPropertyId = resolveTabPropertyId({
        allowed: me?.property_ids,
        serverActive: me?.active_property_id,
        pinned: readPinnedPropertyId(),
      });
      writePinnedPropertyId(tabPropertyId);
      // Set the request header BEFORE setUser: React runs child effects
      // before this provider's effects, so pages would otherwise fire
      // their first queries without it (and land on the server's shared
      // property instead of this tab's).
      setScopedPropertyId(tabPropertyId);
      // The interface follows this tab's property. Set before setUser so
      // the first render after it is already in the right language.
      setLanguage(
        (me?.properties || []).find((p) => p.id === tabPropertyId)?.language,
      );
      // Everything that reads `user.active_property_id` (sidebar label,
      // browser tab title, Research page) now shows this tab's property.
      setUser({ ...me, active_property_id: tabPropertyId });
    } catch {
      setScopedPropertyId("");
      setLanguage(browserLanguage(window.navigator?.language));
      setUser(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    const res = await api.authLogin(email, password);
    if (res.requires_2fa) {
      setPending2fa({ email: res.user?.email || email });
      return { requires_2fa: true };
    }
    // Hit /me so we get the full payload (properties, role, etc.)
    await refresh();
    setPending2fa(null);
    return { requires_2fa: false };
  };

  const verify2fa = async (code) => {
    await api.authVerify2fa(code);
    await refresh();
    setPending2fa(null);
  };

  const switchProperty = async (propertyId) => {
    await api.authSwitchProperty(propertyId);
    // Pin THIS tab to the new property. The server-side switch still
    // happens too, so a brand-new tab opens on the last property picked.
    writePinnedPropertyId(propertyId);
    await refresh();
  };

  const logout = async () => {
    try {
      await api.authLogout();
    } finally {
      setScopedPropertyId("");
      setLanguage(browserLanguage(window.navigator?.language));
      setUser(false);
      setPending2fa(null);
    }
  };

  const cancel2fa = () => setPending2fa(null);

  return (
    <AuthContext.Provider
      value={{ user, pending2fa, login, verify2fa, logout, cancel2fa, refresh, setUser, switchProperty }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
